import type { ReactNode } from "react";

// Chip / StatusPill — DS §4.2. Five roles × {solid, filled}. radius-pill,
// 600/12, uppercase on --tracking-eyebrow. Canonical tokens only.
//
// UPPERCASE because a chip labels a state ("ONBOARDED", "NOT SIGNED") and
// uppercase reads as a label rather than as prose set in a coloured box.
//
// The leading dot is OPT-IN and off by default. It was removed outright on this
// branch, on the reasoning that it was only ever passed alongside `solid`, where
// it restated in a coloured circle exactly what the coloured fill already said,
// and against uppercase it read as a bullet introducing a list. That reasoning
// still holds for the chips that dropped it — none of them ask for it back. It
// is retained as a prop, rather than deleted, because the therapist calendar
// attendee row (`CalendarEventPanel`) was written against it after this branch
// forked and renders it today. Default-false, so no chip that does not ask for a
// dot gains one.
//
// No `outline` variant. It was the quiet option for a chip inside a card, but
// it was also the variant whose border silently lost a specificity fight and
// rendered nothing for four of five roles — so most of its call sites were
// showing bare coloured text, not the quiet pill they asked for. Two fills is
// the whole vocabulary now: `solid` for a state, `filled` for a saturated
// badge. Anything that wants to be quieter should use a quieter ROLE.
//
// RoleTag and SeverityChip lived here too and are gone. RoleTag was an
// info-outline pill spelled as its own component; it is `<Chip role="info">`.
// SeverityChip was a white-on-solid uppercase pill that no production call site
// ever reached — `<Alert>` rendered it only when passed `severity`, which
// nothing did.

export type ChipRole = "success" | "warning" | "danger" | "info" | "neutral";

export type ChipVariant = "solid" | "filled";

export interface ChipProps {
    role?: ChipRole;
    variant?: ChipVariant;
    /** Leading 6px dot in currentColor. Off by default. */
    dot?: boolean;
    /** Optional leading glyph (e.g. a padlock on a private scope). Decorative —
     *  the label carries the meaning — and inherits the pill's currentColor. */
    icon?: ReactNode;
    children: ReactNode;
    className?: string;
    /** Back-compat alias for `role` (gen-1 atoms/Chip prop name). `role` wins
     *  if both are passed. */
    status?: ChipRole;
    /** Back-compat alias for `variant` (gen-1 atoms/Chip prop name). `variant`
     *  wins if both are passed. */
    fill?: ChipVariant;
    /** Test hook forwarded to the rendered span (gen-1 back-compat). */
    "data-testid"?: string;
}

const SOLID: Record<ChipRole, string> = {
    success: "bg-success-bg text-success",
    warning: "bg-warning-bg text-warning",
    danger: "bg-danger-bg text-danger",
    info: "bg-info-bg text-info",
    neutral: "bg-neutral-bg text-secondary",
};

// `filled` — saturated role fill + white text, for a badge that has to carry
// across a card (AlertCard's severity). This is the shape SeverityChip used to
// be, which is why it is uppercase too.
const FILLED: Record<ChipRole, string> = {
    success: "bg-success text-white",
    warning: "bg-warning text-white",
    danger: "bg-danger text-white",
    info: "bg-info text-white",
    neutral: "bg-neutral text-white",
};

const FILL_CLASSES: Record<ChipVariant, Record<ChipRole, string>> = {
    solid: SOLID,
    filled: FILLED,
};

export function Chip({
    role,
    variant,
    dot = false,
    icon,
    children,
    className,
    status,
    fill,
    "data-testid": dataTestId,
}: ChipProps) {
    // ds/ API wins on conflict; gen-1 prop names (status/fill) are aliases.
    const resolvedRole: ChipRole = role ?? status ?? "neutral";
    const resolvedVariant: ChipVariant = variant ?? fill ?? "solid";
    const classes = [
        // Design QA tightened this from px-2.5/py-[3px]; the dot gap is separate
        // and survives it.
        // `py-1` with `leading-none` below, not `py-0.5` with `leading-[1.4]`:
        // the old pair reserved descender space that uppercase never uses, so
        // every chip in the product rendered its caps ~1.8px high inside its own
        // pill. The taller padding offsets the shorter line box, which keeps the
        // pill at 20px against the 20.8px it used to be — a centring fix, not a
        // resize.
        // `pl-2` + a right padding one tracking step shorter, not `px-2`:
        // --tracking-eyebrow adds its space after the LAST letter as well as
        // between letters, so numerically even padding leaves the glyphs
        // sitting ~1.2px left of centre in every uppercase chip. Optical
        // centring, which is the only kind a reader sees.
        "inline-flex items-center rounded-[var(--radius-pill)] pl-2 pr-[calc(0.5rem-0.1em)] py-1",
        // Only when a dot is actually drawn: with a lone text child `gap` has no
        // effect anyway, but keeping it conditional makes that explicit.
        (dot || icon) && "gap-1.5",
        // --tracking-eyebrow, not a fresh value: this is the job that token
        // already names, uppercase 12/600 that needs opening up. The portal's
        // gen-1 atoms/Chip has always used it, so the two now track identically.
        "font-sans text-xs font-semibold leading-none uppercase tracking-[var(--tracking-eyebrow)]",
        FILL_CLASSES[resolvedVariant][resolvedRole],
        className,
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <span className={classes} data-testid={dataTestId}>
            {dot && <span data-dot className="h-1.5 w-1.5 shrink-0 rounded-full bg-current" />}
            {icon && (
                <span aria-hidden="true" className="inline-flex shrink-0">
                    {icon}
                </span>
            )}
            {children}
        </span>
    );
}
